/**
 * CrossFrameworkIntelligence — Zero hardcoded data. Pure rendering layer.
 * 
 * 12 API calls, 0 regulatory constants.
 * Static data (matrix, overlap, KPIs) cached for 5 minutes.
 * Live data (proof points, accuracy, auto-link) always fresh.
 * 
 * If Framework #15 is added to the backend, it appears here automatically.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from '../services/api_service';


const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30", cardHover: "#162040",
  border: "#1a2744", borderHi: "#2a3a5c", text: "#e2e8f0", muted: "#6b7a96", dim: "#384660",
  blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981", purple: "#8b5cf6",
  cyan: "#06b6d4", red: "#ef4444", pink: "#ec4899", teal: "#14b8a6", orange: "#f97316",
};
const FLAG_COLORS = { R: C.emerald, C: C.amber, V: C.purple };
const FLAG_LABELS = { R: "Required", C: "Conditional", V: "Voluntary" };

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api/v1/cfi";
const _cache = {};

async function cfiFetch(path, options) {
  const key = options?.method === "POST" ? null : path;
  if (key && _cache[key]?.ts > Date.now() - 300000) return _cache[key].data;
  try {
    const headers = { "Content-Type": "application/json" };
    const token = localStorage.getItem("token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}${path}`, {
      headers, ...options,
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const data = await res.json();
    if (key) _cache[key] = { data, ts: Date.now() };
    return data;
  } catch (e) { console.error(`CFI fetch ${path}:`, e); return null; }
}

function Loader() { return <div style={{ padding: 32, textAlign: "center", color: C.dim, fontSize: 11 }} data-testid="cfi-loader">Loading intelligence data...</div>; }

function Pill({ color, children }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 7px", borderRadius: 99, fontSize: 9, fontWeight: 600, background: color + "18", color, border: `1px solid ${color}33`, lineHeight: "16px", whiteSpace: "nowrap" }}>{children}</span>;
}

const thS = { padding: "6px 4px", textAlign: "left", fontSize: 8, color: C.muted, fontWeight: 600, letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` };
const tdS = { padding: "5px 4px", fontSize: 9, color: C.muted, borderBottom: `1px solid ${C.border}22` };

// ═══════════════════════════════════════════════════════════════
// TAB 1: FRAMEWORK MATRIX HEATMAP
// ═══════════════════════════════════════════════════════════════
function MatrixTab() {
  const [data, setData] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const [filterFw, setFilterFw] = useState("");

  useEffect(() => { cfiFetch("/matrix").then(setData); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let items = data.topics;
    if (filterCat) items = items.filter(m => m.category.includes(filterCat));
    if (filterFw) items = items.filter(m => m.frameworks[filterFw]);
    return items;
  }, [data, filterCat, filterFw]);

  const categories = useMemo(() => data ? [...new Set(data.topics.map(t => t.category))] : [], [data]);

  if (!data) return <Loader />;
  const fws = data.frameworks;

  return (
    <div data-testid="cfi-matrix-tab">
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} data-testid="matrix-category-filter"
          style={{ padding: "4px 8px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 10 }}
          aria-label="Filter by category">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterFw} onChange={e => setFilterFw(e.target.value)} data-testid="matrix-framework-filter"
          style={{ padding: "4px 8px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 10 }}
          aria-label="Filter by framework">
          <option value="">All Frameworks</option>
          {fws.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <span style={{ fontSize: 9, color: C.dim, marginLeft: "auto" }} data-testid="matrix-topic-count">{filtered.length} topics</span>
        <div style={{ display: "flex", gap: 6 }}>
          {Object.entries(FLAG_LABELS).map(([k, v]) => <Pill key={k} color={FLAG_COLORS[k]}>{k} = {v}</Pill>)}
        </div>
      </div>
      <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${C.border}` }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }} role="grid" aria-label="Cross-framework matrix heatmap" data-testid="matrix-table">
          <thead>
            <tr style={{ background: C.surface }}>
              <th style={{ ...thS, minWidth: 140 }}>Topic</th>
              {fws.map(fw => <th key={fw} style={{ ...thS, minWidth: 28, writingMode: "vertical-rl", transform: "rotate(180deg)", height: 80 }}>{fw}</th>)}
              <th style={thS}>#</th>
              <th style={{ ...thS, minWidth: 40 }}>Reuse</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? C.card : C.surface }}>
                <td style={{ ...tdS, fontWeight: 500, color: C.text }}>{m.topic}</td>
                {fws.map(fw => {
                  const flag = m.frameworks[fw];
                  return <td key={fw} style={{ ...tdS, textAlign: "center", background: flag ? (FLAG_COLORS[flag] || C.dim) + "22" : "transparent", color: flag ? FLAG_COLORS[flag] || C.muted : C.dim + "44" }}>{flag || "\u00b7"}</td>;
                })}
                <td style={{ ...tdS, textAlign: "center", fontWeight: 700, color: m.fw_count >= 10 ? C.emerald : m.fw_count >= 6 ? C.amber : C.muted }}>{m.fw_count}</td>
                <td style={{ ...tdS, textAlign: "center", color: C.cyan }}>{m.reuse_pct}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 2: OVERLAP MATRIX
// ═══════════════════════════════════════════════════════════════
function OverlapTab() {
  const [data, setData] = useState(null);
  useEffect(() => { cfiFetch("/overlap").then(setData); }, []);
  if (!data) return <Loader />;

  const fws = data.frameworks;
  return (
    <div data-testid="cfi-overlap-tab">
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 10 }}>% of <strong style={{ color: C.text }}>ROW</strong> framework requirements satisfiable by evidence from <strong style={{ color: C.text }}>COLUMN</strong> framework</div>
      <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${C.border}` }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }} role="grid" aria-label="Framework overlap matrix" data-testid="overlap-table">
          <thead>
            <tr style={{ background: C.surface }}>
              <th style={{ ...thS, minWidth: 80 }}></th>
              {fws.map(fw => <th key={fw} style={{ ...thS, minWidth: 40, writingMode: "vertical-rl", transform: "rotate(180deg)", height: 80 }}>{fw}</th>)}
            </tr>
          </thead>
          <tbody>
            {fws.map((rowFw, ri) => (
              <tr key={rowFw} style={{ background: ri % 2 === 0 ? C.card : C.surface }}>
                <td style={{ ...tdS, fontWeight: 600, color: C.text }}>{rowFw}</td>
                {fws.map(colFw => {
                  const val = data.matrix[rowFw]?.[colFw] || "0%";
                  const pct = parseInt(val);
                  const bg = rowFw === colFw ? C.blue + "33" : pct >= 50 ? C.emerald + "22" : pct >= 20 ? C.amber + "15" : "transparent";
                  const col = pct >= 50 ? C.emerald : pct >= 20 ? C.amber : pct > 0 ? C.muted : C.dim + "44";
                  return <td key={colFw} style={{ ...tdS, textAlign: "center", background: bg, color: col, fontWeight: pct >= 50 ? 700 : 400 }}>{val}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 3: KPI INTELLIGENCE MAP
// ═══════════════════════════════════════════════════════════════
function KpiTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");

  useEffect(() => { cfiFetch("/kpis").then(setData); }, []);
  if (!data) return <Loader />;

  const filtered = data.kpis.filter(k =>
    (!search || k.name.toLowerCase().includes(search.toLowerCase())) &&
    (!catFilter || k.category === catFilter)
  );

  return (
    <div data-testid="cfi-kpi-tab">
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search KPIs..."
          style={{ padding: "4px 8px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 10, flex: "1 1 200px" }}
          aria-label="Search KPIs" data-testid="kpi-search-input" />
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          style={{ padding: "4px 8px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 10 }}
          aria-label="Filter by category" data-testid="kpi-category-filter">
          <option value="">All</option>
          {data.categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span style={{ fontSize: 9, color: C.dim }} data-testid="kpi-count">{filtered.length} KPIs</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {filtered.map((k, i) => (
          <div key={i} style={{ padding: "8px 10px", borderRadius: 6, background: C.card, border: `1px solid ${C.border}` }} data-testid={`kpi-card-${i}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{k.name}</span>
              <Pill color={C.cyan}>{k.unit}</Pill>
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 3 }}>
              <Pill color={C.blue}>ESRS {k.esrs_ref}</Pill>
              {Object.entries(k.framework_refs || {}).slice(0, 6).map(([fw, ref]) => (
                <Pill key={fw} color={C.muted}>{fw}: {ref}</Pill>
              ))}
              {Object.keys(k.framework_refs || {}).length > 6 && <Pill color={C.dim}>+{Object.keys(k.framework_refs).length - 6} more</Pill>}
            </div>
            <div style={{ fontSize: 9, color: C.dim }}>{k.category} . {k.fw_count} frameworks . {k.frequency}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 4: CSRD-CSDDD DEEP INTERSECTION
// ═══════════════════════════════════════════════════════════════
function IntersectionTab() {
  const [data, setData] = useState(null);
  useEffect(() => { cfiFetch("/csrd-csddd").then(setData); }, []);
  if (!data) return <Loader />;

  return (
    <div data-testid="cfi-intersection-tab">
      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <div style={{ padding: "10px 16px", borderRadius: 8, background: C.emerald + "15", border: `1px solid ${C.emerald}33`, flex: 1 }} data-testid="avg-overlap-card">
          <div style={{ fontSize: 22, fontWeight: 700, color: C.emerald }}>{data.avg_overlap}%</div>
          <div style={{ fontSize: 9, color: C.muted }}>Average overlap</div>
        </div>
        <div style={{ padding: "10px 16px", borderRadius: 8, background: C.blue + "15", border: `1px solid ${C.blue}33`, flex: 1 }} data-testid="pair-count-card">
          <div style={{ fontSize: 22, fontWeight: 700, color: C.blue }}>{data.total}</div>
          <div style={{ fontSize: 9, color: C.muted }}>Requirement pairs</div>
        </div>
      </div>
      {data.intersections.map((item, i) => {
        const pct = parseInt(item.overlap_pct);
        const barColor = pct >= 70 ? C.emerald : pct >= 50 ? C.amber : C.red;
        return (
          <div key={i} style={{ padding: 10, borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, marginBottom: 6 }} data-testid={`intersection-pair-${i}`}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{item.csrd_req}</div>
                <div style={{ fontSize: 9, color: C.muted }}>ESRS {item.esrs_ref} <span style={{ color: C.dim }}>{"<->"}</span> CSDDD {item.csddd_article}</div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: barColor }}>{item.overlap_pct}</div>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: C.dim + "33", marginBottom: 6 }}>
              <div style={{ height: "100%", borderRadius: 2, background: barColor, width: `${pct}%`, transition: "width 0.5s" }} />
            </div>
            <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.4 }}>{item.csddd_req}</div>
            <div style={{ fontSize: 9, color: C.dim, marginTop: 4 }}>Evidence: {item.evidence_shared?.substring(0, 100)}...</div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 5: EVIDENCE REUSE ANALYTICS (LIVE DATA)
// ═══════════════════════════════════════════════════════════════
function ReuseTab() {
  const [proofPoints, setProofPoints] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [reuseData, setReuseData] = useState(null);
  const [autoLinkResult, setAutoLinkResult] = useState(null);
  const [demoTopic, setDemoTopic] = useState("Scope 1 GHG Emissions");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    cfiFetch("/proof-points").then(setProofPoints);
    cfiFetch("/auto-link/accuracy").then(setAccuracy);
    cfiFetch("/reuse-analytics").then(setReuseData);
  }, []);

  const runAutoLink = useCallback(async () => {
    setLoading(true);
    const result = await cfiFetch("/auto-link", { method: "POST", body: { evidence_topic: demoTopic, source_framework: "CSRD/ESRS" } });
    setAutoLinkResult(result);
    setLoading(false);
  }, [demoTopic]);

  const validateLink = useCallback(async (topic, fw, validation) => {
    await cfiFetch("/auto-link/validate", { method: "POST", body: { evidence_topic: topic, framework: fw, validation } });
    const newAcc = await cfiFetch("/auto-link/accuracy");
    setAccuracy(newAcc);
  }, []);

  return (
    <div data-testid="cfi-reuse-tab">
      {/* Proof Points (live) */}
      {proofPoints && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }} data-testid="proof-points-section">
          <div style={{ fontSize: 11, fontWeight: 700, color: C.cyan, letterSpacing: 0.5 }}>LIVE PLATFORM METRICS</div>
          {proofPoints.proof_points.map((pp, i) => (
            <div key={i} style={{ padding: "8px 12px", borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }} data-testid={`proof-point-${i}`}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: C.text }}>{pp.category}</div>
                <div style={{ fontSize: 9, color: C.dim }}>{pp.detail?.substring(0, 80)}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.emerald }}>{pp.metric}</div>
                <div style={{ fontSize: 8, color: C.dim }}>{pp.data_source}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Auto-link accuracy */}
      {accuracy && accuracy.total_validations > 0 && (
        <div style={{ padding: 10, borderRadius: 8, background: C.purple + "11", border: `1px solid ${C.purple}33`, marginBottom: 12 }} data-testid="accuracy-section">
          <div style={{ fontSize: 11, fontWeight: 700, color: C.purple, marginBottom: 4 }}>AUTO-LINK ACCURACY (from validation feedback)</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div><span style={{ fontSize: 20, fontWeight: 700, color: C.purple }}>{accuracy.accuracy_pct}%</span><span style={{ fontSize: 9, color: C.muted }}> accuracy</span></div>
            <div><span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{accuracy.total_validations}</span><span style={{ fontSize: 9, color: C.muted }}> validations</span></div>
            <div><span style={{ fontSize: 14, fontWeight: 600, color: C.emerald }}>{accuracy.accepted}</span><span style={{ fontSize: 9, color: C.muted }}> accepted</span></div>
            <div><span style={{ fontSize: 14, fontWeight: 600, color: C.red }}>{accuracy.rejected}</span><span style={{ fontSize: 9, color: C.muted }}> rejected</span></div>
          </div>
        </div>
      )}

      {/* Auto-link demo */}
      <div style={{ padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.blue}33`, marginBottom: 12 }} data-testid="autolink-demo-section">
        <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 8 }}>AUTO-LINK DEMO</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <input value={demoTopic} onChange={e => setDemoTopic(e.target.value)}
            style={{ flex: 1, padding: "5px 8px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 10 }}
            aria-label="Evidence topic for auto-link" data-testid="autolink-topic-input" />
          <button onClick={runAutoLink} disabled={loading} data-testid="autolink-submit-btn"
            style={{ padding: "5px 12px", borderRadius: 5, border: `1px solid ${C.blue}`, background: C.blue + "18", color: C.blue, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>
            {loading ? "Linking..." : "Auto-Link"}
          </button>
        </div>
        {autoLinkResult && (
          <div data-testid="autolink-results">
            <div style={{ fontSize: 9, color: C.muted, marginBottom: 6 }}>
              {autoLinkResult.total_links} frameworks linked . {autoLinkResult.high_confidence} high confidence . Source: {autoLinkResult.confidence_source}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {autoLinkResult.auto_linked?.slice(0, 8).map((link, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 4, background: C.surface }} data-testid={`autolink-row-${i}`}>
                  <Pill color={FLAG_COLORS[link.flag] || C.muted}>{link.flag}</Pill>
                  <span style={{ fontSize: 10, fontWeight: 500, color: C.text, flex: 1 }}>{link.framework}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: link.confidence >= 0.7 ? C.emerald : link.confidence >= 0.4 ? C.amber : C.red }}>
                    {Math.round(link.confidence * 100)}%
                  </span>
                  {link.validation_count > 0 && <span style={{ fontSize: 8, color: C.purple }}>({link.validation_count} validations)</span>}
                  <button onClick={() => validateLink(autoLinkResult.evidence_topic, link.framework, "accepted")}
                    style={{ padding: "1px 4px", borderRadius: 3, border: "none", background: C.emerald + "22", color: C.emerald, fontSize: 8, cursor: "pointer" }}
                    aria-label={`Accept link to ${link.framework}`} data-testid={`validate-accept-${i}`}>V</button>
                  <button onClick={() => validateLink(autoLinkResult.evidence_topic, link.framework, "rejected")}
                    style={{ padding: "1px 4px", borderRadius: 3, border: "none", background: C.red + "22", color: C.red, fontSize: 8, cursor: "pointer" }}
                    aria-label={`Reject link to ${link.framework}`} data-testid={`validate-reject-${i}`}>X</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reuse analytics */}
      {reuseData && (
        <div style={{ padding: 10, borderRadius: 8, background: C.surface, border: `1px solid ${C.border}` }} data-testid="reuse-analytics-section">
          <div style={{ fontSize: 11, fontWeight: 700, color: C.teal, marginBottom: 6 }}>REUSE ANALYTICS</div>
          <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
            <div><span style={{ fontSize: 18, fontWeight: 700, color: C.teal }}>{reuseData.avg_reuse_pct}%</span><span style={{ fontSize: 9, color: C.muted }}> avg reuse</span></div>
            <div><span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{reuseData.avg_fw_per_topic}</span><span style={{ fontSize: 9, color: C.muted }}> avg FW/topic</span></div>
          </div>
          {Object.entries(reuseData.reuse_buckets || {}).map(([bucket, bdata]) => (
            <div key={bucket} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Pill color={bucket === "high" ? C.emerald : bucket === "medium" ? C.amber : C.red}>{bucket}</Pill>
              <span style={{ fontSize: 10, color: C.text }}>{bdata.count} topics</span>
              <span style={{ fontSize: 9, color: C.dim }}>avg {bdata.avg_fw_count} FWs</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function CrossFrameworkIntelligence() {
  const [tab, setTab] = useState("matrix");
  const [stats, setStats] = useState(null);

  useEffect(() => { cfiFetch("/stats").then(setStats); }, []);

  const tabs = [
    { id: "matrix", label: "Framework Matrix" },
    { id: "overlap", label: "Overlap Matrix" },
    { id: "kpis", label: "KPI Map" },
    { id: "intersection", label: "CSRD<>CSDDD" },
    { id: "reuse", label: "Evidence Reuse" },
  ];

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Segoe UI', system-ui, sans-serif" }} data-testid="cross-framework-intelligence">
      <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg + "f0", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff" }}>D</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700 }} data-testid="cfi-title">Cross-Framework Intelligence</div>
            <div style={{ fontSize: 8, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase" }} data-testid="cfi-subtitle">
              {stats ? `${stats.total_mapping_decisions} mappings . ${stats.total_topics} topics . ${stats.total_frameworks} frameworks . ${stats.total_kpis} KPIs` : "Loading..."}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`cfi-tab-${t.id}`}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 5, border: `1px solid ${tab === t.id ? C.blue : C.border}`, background: tab === t.id ? C.blue + "18" : "transparent", color: tab === t.id ? C.blue : C.muted, fontSize: 10, fontWeight: 600, cursor: "pointer" }}
              aria-pressed={tab === t.id}>{t.label}</button>
          ))}
          {/* Deep link to the live per-entity view (this page is the static reference) */}
          <Link to="/coverage" data-testid="cfi-live-coverage-link"
            style={{ marginLeft: "auto", padding: "4px 10px", borderRadius: 5, border: `1px solid ${C.emerald}66`, background: C.emerald + "14", color: C.emerald, fontSize: 10, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}>
            View live evidence coverage →
          </Link>
        </div>
      </div>

      <div style={{ padding: "16px 20px", maxWidth: 1280, margin: "0 auto" }}>
        {tab === "matrix" && <MatrixTab />}
        {tab === "overlap" && <OverlapTab />}
        {tab === "kpis" && <KpiTab />}
        {tab === "intersection" && <IntersectionTab />}
        {tab === "reuse" && <ReuseTab />}
      </div>
    </div>
  );
}
